import React, { useEffect, useState } from "react";
import { ConfettiLayer } from "./effects/ConfettiLayer";
import { RealCar } from "./cars/RealCar";

const PositionBadge = ({ pos, endRacing = false }) => {
  const s =
    pos === 1 && endRacing
      ? {
        from: "from-yellow-300",
        to: "to-amber-600",
        ring: "ring-yellow-300/70",
        text: "text-yellow-900",
        glow: "shadow-[0_0_18px_rgba(245,158,11,0.55)]",
      }
      : pos === 2 && endRacing
        ? {
          from: "from-slate-200",
          to: "to-slate-500",
          ring: "ring-slate-300/60",
          text: "text-slate-900",
          glow: "shadow-[0_0_14px_rgba(203,213,225,0.45)]",
        }
        : pos === 3 && endRacing
          ? {
            from: "from-orange-400",
            to: "to-amber-800",
            ring: "ring-orange-400/60",
            text: "text-amber-900",
            glow: "shadow-[0_0_14px_rgba(251,146,60,0.45)]",
          }
          : {
            from: "from-slate-300",
            to: "to-slate-600",
            ring: "ring-slate-400/40",
            text: "text-slate-900",
            glow: "",
          };

  return (
    <div className="relative mx-auto w-10 h-10">
      <div
        className={`absolute inset-0 rounded-full bg-gradient-to-b ring-2 ${s.from} ${s.to} ${s.ring} ${s.glow}`}
      />
      <div
        className={`relative z-10 flex h-full items-center justify-center font-black text-[12px] ${s.text}`}
      >
        {pos}
      </div>
    </div>
  );
};

const EmptyStandings = () => (
  <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-gray-900/70 to-black/80 p-10 text-center">
    <div
      className="pointer-events-none absolute inset-0 opacity-[0.07]"
      style={{
        backgroundImage:
          "linear-gradient(90deg, rgba(255,255,255,.12) 50%, transparent 50%), linear-gradient(0deg, rgba(255,255,255,.12) 50%, transparent 50%)",
        backgroundSize: "24px 24px, 24px 24px",
        animation: "flag 18s linear infinite",
      }}
    />
    <div className="relative z-10">
      <div className="mx-auto mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-yellow-500/10 ring-2 ring-yellow-400/30">
        <span className="text-3xl">🏆</span>
      </div>
      <h3 className="text-2xl font-extrabold tracking-tight">
        
      </h3>
      <p className="mt-2 text-white/70">Buscando conductores...</p>
      <div className="mt-2">
        <img
          src={"/cars/ferrari.gif"}
          alt="F1 car"
          width={"20%"}
          style={{ margin: "0 auto" }}
        />
      </div>
    </div>
  </div>
);

const NoResults = ({ query }) => (
  <div className="rounded-2xl border border-white/10 bg-black/40 p-8 text-center">
    <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/5">
      <span className="text-xl">🔎</span>
    </div>
    <p className="text-white/80">
      No hay resultados para{" "}
      <span className="font-semibold text-white">“{query}”</span>.
    </p>
    <p className="text-white/50 text-sm mt-1">
      Prueba con otro nombre o limpia el buscador.
    </p>
  </div>
);

export const StandingsTable = ({ data, view = "", finishSession }) => {
  const [search, setSearch] = useState("");
  const [endRacing] = useState(true);
  const [standings, setStandings] = useState(data);

  // 🔹 Paginado
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  useEffect(() => {
    const i = setInterval(() => setStandings([...data]), 10000);
    return () => clearInterval(i);
  }, [data]);

  const filtered = standings.filter((d) =>
    d.name.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / rowsPerPage);
  const start = (currentPage - 1) * rowsPerPage;
  const paginatedData = filtered.slice(start, start + rowsPerPage);

  // reset page si cambian resultados
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  return (
    <section id="standings" className="w-full py-12 px-8 bg-gray-950 text-white">


      {finishSession && (
        <>
          <hr />
          <div className="mt-6 text-xl md:text-3xl font-black tracking-wider text-white text-center">
            CARRERA FINALIZADA
          </div>
          <div className="mt-2 text-xl md:text-3xl font-black tracking-wider text-hitch text-center mb-3">
            ¡GRACIAS POR PARTICIPAR!
          </div>
          <hr />
        </>
      )}

      <h2 className="text-3xl font-bold mb-6 text-hitch mt-6">
        📋 Tabla de posiciones
      </h2>

      <input
        type="text"
        placeholder="Buscar conductor..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mb-6 px-4 py-2 rounded-lg border border-gray-700 bg-black text-white w-full max-w-md"
      />

      {standings.length === 0 ? (
        <EmptyStandings />
      ) : filtered.length === 0 ? (
        <NoResults query={search} />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-gray-800 text-gray-300">
                  <th className="p-3 text-center w-[76px]">Posición</th>
                  <th className="p-3 text-center w-[150px]">Conductor N°</th>
                  <th className="p-3 text-left">Conductor</th>
                  <th className="p-3 text-left">Empresa</th>
                  <th className="p-3 text-center">S1</th>
                  <th className="p-3 text-center">S2</th>
                  <th className="p-3 text-center">S3</th>
                  <th className="p-3 text-center">Mejor vuelta</th>
                  <th className="p-3 text-center">Tiempo carrera</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((d, i) => {
                  const pos = start + i + 1;
                  const highlight =
                    pos === 1
                      ? "bg-gradient-to-r from-amber-400/10 to-transparent"
                      : pos === 2
                        ? "bg-gradient-to-r from-slate-300/10 to-transparent"
                        : pos === 3
                          ? "bg-gradient-to-r from-orange-400/10 to-transparent"
                          : "";

                  return (
                    <tr
                      key={i}
                      className={`border-b border-gray-700 hover:bg-gray-800/70 transition ${endRacing ? highlight : ""
                        }`}
                    >
                      <td className="p-3 text-center">
                        <PositionBadge
                          pos={endRacing ? pos : "--"}
                          endRacing={endRacing}
                        />
                      </td>
                      <td className="p-3 text-center">{pos}</td>
                      <td className="p-3 font-semibold">{d.name}</td>
                      <td className="p-3 italic text-sm text-gray-400">
                        {d.team}
                      </td>
                      <td className="p-3 text-center">
                        {d.laps?.[0]?.lap_time || "--"}
                      </td>
                      <td className="p-3 text-center">
                        {d.laps?.[1]?.lap_time || "--"}
                      </td>
                      <td className="p-3 text-center">
                        {d.laps?.[2]?.lap_time || "--"}
                      </td>
                      <td className="p-3 text-center text-green-400 font-bold">
                        {d.best}
                      </td>
                      <td className="p-3 text-center">
                        {endRacing ? d.total : "---"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* 🔹 Controles de paginación */}
          <div className="flex justify-center items-center gap-2 mt-6">
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded bg-gray-800 hover:bg-gray-700 disabled:opacity-40"
            >
              ⬅ Anterior
            </button>

            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-1 rounded ${currentPage === i + 1
                    ? "bg-hitch text-white"
                    : "bg-gray-800 hover:bg-gray-700"
                  }`}
              >
                {i + 1}
              </button>
            ))}

            <button
              onClick={() =>
                setCurrentPage((p) => Math.min(p + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded bg-gray-800 hover:bg-gray-700 disabled:opacity-40"
            >
              Siguiente ➡
            </button>
          </div>
        </>
      )}
    </section>
  );
};
